import React from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, Award, HeartHandshake, Instagram, Facebook, Linkedin, MessageCircle, ExternalLink } from 'lucide-react';
import { FOUNDER_PHOTO_URL, UKGM_LINKS } from '@/lib/ui';
import { useSEO } from '@/lib/useSEO';

const OTHER_TRUSTEES = [
  { name: 'Mr. Dilip Jaganath Maity', role: 'Trustee' },
  { name: 'Ms. Vijaya Vishwajit Rathod', role: 'Trustee' },
];

export default function About() {
  const { t } = useTranslation();
  useSEO({ title: 'About', description: 'About Universal Krishna Gaukul Mission — a Public Charitable Trust registered under the Bombay Public Trusts Act 1950 with 12A, 80G, NGO Darpan and CSR-1 registrations.' });

  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="pt-16 pb-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-xs tracking-widest uppercase text-ukgm-gold font-bold">About</div>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl text-ukgm-blue font-semibold leading-tight">
          Universal Krishna Gaukul Mission
        </h1>
        <p className="mt-4 text-ukgm-gold font-semibold tracking-widest uppercase text-sm">A Registered Public Charitable Trust in India</p>
        <p className="mt-6 max-w-3xl text-ukgm-slate text-lg leading-relaxed">
          Universal Krishna Gaukul Mission (UKGM) is a Public Charitable Trust registered under the Bombay Public Trusts Act, 1950. The Trust holds statutory registrations under Section 12A and Section 80G of the Income Tax Act, is registered on NGO Darpan and is an eligible CSR partner (CSR-1) recognised by the Ministry of Corporate Affairs, Government of India. UKGM operates all over India.
        </p>
      </section>

      {/* VISION & MISSION */}
      <section className="py-14 bg-white border-y border-ukgm-gold/20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-8">
          <div>
            <div className="text-xs tracking-widest uppercase text-ukgm-gold font-bold">{t('mission.vision_label')}</div>
            <p className="mt-4 font-serif text-2xl md:text-3xl text-ukgm-blue leading-snug">{t('mission.vision_body')}</p>
          </div>
          <div>
            <div className="text-xs tracking-widest uppercase text-ukgm-gold font-bold">{t('mission.mission_label')}</div>
            <p className="mt-4 font-serif text-2xl md:text-3xl text-ukgm-blue leading-snug">{t('mission.mission_body')}</p>
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="py-16 md:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-xs tracking-widest uppercase text-ukgm-gold font-bold">Founder</div>
        <h2 className="mt-2 font-serif text-3xl md:text-4xl text-ukgm-blue font-semibold">Meet our Founder</h2>

        <div className="mt-10 grid lg:grid-cols-12 gap-10 items-start" data-testid="founder-card">
          <div className="lg:col-span-4">
            <div className="relative rounded-2xl overflow-hidden border border-ukgm-gold/40 gold-glow bg-gradient-to-br from-ukgm-cream via-white to-ukgm-goldLight aspect-[4/5]">
              <img
                src={FOUNDER_PHOTO_URL}
                alt="Mr. Udhhab Madhav Maharaj — Founder, Universal Krishna Gaukul Mission"
                className="absolute inset-0 w-full h-full object-cover object-top"
                loading="lazy"
                data-testid="founder-photo"
              />
            </div>
          </div>
          <div className="lg:col-span-8">
            <h3 className="font-serif text-3xl md:text-4xl text-ukgm-blue font-semibold">Mr. Udhhab Madhav Maharaj</h3>
            <div className="mt-2 text-ukgm-gold font-semibold tracking-widest uppercase text-sm">Founder & Social Reformer</div>
            <div className="mt-1 text-ukgm-slate text-sm">Managing Trustee, Settler & President — Universal Krishna Gaukul Mission</div>

            <div className="mt-6 space-y-4 text-ukgm-charcoal leading-relaxed">
              <p>
                <em>"I am Udhhab Madhav Maharaj, Founder of Universal Krishna Gaukul Mission (UKGM), a Registered Public Charitable Trust based in Mumbai, India."</em>
              </p>
              <p>
                Our mission is dedicated to creating sustainable social impact through elderly care, healthcare, education, animal welfare, environmental sustainability, community development, women and child welfare, skill development, and humanitarian initiatives.
              </p>
              <p>
                I am committed to building meaningful partnerships that transform lives, strengthen communities, and contribute to a more inclusive, compassionate, and sustainable future.
              </p>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="founder-socials">
              <a href={UKGM_LINKS.visiting_card} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-ukgm-blue text-white text-sm font-semibold px-4 py-2 rounded-full hover:bg-ukgm-blueDark">
                <ExternalLink className="w-4 h-4 text-ukgm-gold" /> Digital visiting card
              </a>
              <SocialIconLink href={UKGM_LINKS.instagram} icon={Instagram} label="Instagram" />
              <SocialIconLink href={UKGM_LINKS.facebook} icon={Facebook} label="Facebook" />
              <SocialIconLink href={UKGM_LINKS.linkedin} icon={Linkedin} label="LinkedIn" />
              <SocialIconLink href={UKGM_LINKS.whatsapp} icon={MessageCircle} label="WhatsApp" />
            </div>
          </div>
        </div>
      </section>

      {/* OTHER TRUSTEES */}
      <section className="py-14 bg-ukgm-cream border-y border-ukgm-gold/20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl md:text-4xl text-ukgm-blue font-semibold">Board of Trustees</h2>
          <p className="mt-2 text-ukgm-slate">Together with our Founder, the Board provides governance and leadership to Universal Krishna Gaukul Mission.</p>
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            {OTHER_TRUSTEES.map((tr, i) => (
              <div key={tr.name} className="rounded-2xl border border-ukgm-gold/30 bg-white p-6 card-lift" data-testid={`trustee-${i}`}>
                <div className="font-serif text-xl text-ukgm-blue font-semibold">{tr.name}</div>
                <div className="mt-1 text-sm text-ukgm-gold font-semibold tracking-wide uppercase">{tr.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REGISTRATIONS */}
      <section className="py-14 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-serif text-3xl md:text-4xl text-ukgm-blue font-semibold">Registrations & Compliance</h2>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-4">
          {[
            { label: 'PAN', value: 'AABTU5738J' },
            { label: '12A', value: 'Provisionally Approved' },
            { label: '80G', value: 'Provisionally Approved' },
            { label: 'CSR-1', value: 'CSR00106731' },
            { label: 'NGO Darpan', value: 'MH/2025/0872011' },
            { label: 'Registered under', value: 'Bombay Public Trusts Act, 1950' },
          ].map(r => (
            <div key={r.label} className="rounded-2xl border border-ukgm-gold/30 bg-white p-5">
              <div className="text-[10px] tracking-widest uppercase text-ukgm-slate font-bold">{r.label}</div>
              <div className="mt-2 text-ukgm-blue font-semibold text-sm break-words">{r.value}</div>
            </div>
          ))}
        </div>
      </section>

      {/* COMPLIANCE ICON STRIP */}
      <section className="py-14 bg-ukgm-blue text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-6">
          {[
            { icon: ShieldCheck, title: '80G Exempt', body: 'Donations to UKGM qualify for tax exemption under Section 80G of the Income Tax Act.' },
            { icon: Award, title: '12A Registered', body: 'The Trust holds Section 12A registration for its income-tax exempt status.' },
            { icon: HeartHandshake, title: 'CSR-1 Approved', body: 'UKGM is registered under CSR-1 (CSR00106731) as an eligible corporate CSR partner.' },
          ].map(c => (
            <div key={c.title} className="bg-white/10 border border-ukgm-gold/40 rounded-2xl p-6">
              <c.icon className="w-8 h-8 text-ukgm-gold" />
              <h3 className="mt-4 font-serif text-2xl font-semibold text-ukgm-gold">{c.title}</h3>
              <p className="mt-2 text-white/80 text-sm">{c.body}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function SocialIconLink({ href, icon: Icon, label }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
      className="w-10 h-10 rounded-full bg-white border border-ukgm-gold/40 text-ukgm-blue flex items-center justify-center hover:bg-ukgm-gold hover:text-ukgm-blue hover:border-ukgm-gold transition-colors"
      aria-label={label} title={label} data-testid={`founder-${label.toLowerCase()}`}>
      <Icon className="w-4 h-4" />
    </a>
  );
}
